import type { CarePlan, Goal, ServiceRequest } from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import { Icon, Stack, Tag, Text, cn } from '@eluve/components';
import {
  GoalAchievementTag,
  formatFhirDate,
  goalProgress,
} from '@eluve/fhir-renderer';

import {
  type LinkedGoal,
  type PhaseStep,
  type TreatedCondition,
  carePlanToken,
  currentPhase,
  linkedGoals,
  planEndDate,
  planPhaseSteps,
  planProgress,
  treatedConditions,
} from '../plan-list';

/**
 * One active care plan of the Overview-tab Care Plans section (ENG-5369,
 * Rev7): a 56px header (title + "Phase N of M" pill) toggling between the
 * collapsed summary (current phase, week progress, treating chips, goal
 * mini-rows) and the expanded detail (treating chips, phase stepper, dated
 * progress, linked goal rows). Conditions and goals cross-link into the
 * sibling sections' URL-driven drawers — the card owns no drawer of its own.
 */
export type CarePlanCardProps = {
  plan: CarePlan;
  /** The loaded request window phase states resolve from (spine sticks). */
  serviceRequests: ServiceRequest[];
  /** The loaded goal window `CarePlan.goal` references resolve against. */
  goals: Goal[];
  /** Today as an ISO date — injected so week math stays pure/testable. */
  todayIsoDate: string;
  expanded: boolean;
  onToggle: (token: string) => void;
  /** Open the Conditions drawer (`?condition=<id>`, owned by that section). */
  onOpenCondition: (token: string) => void;
  /** Open the Goals drawer (`?goal=<id>`, owned by that section). */
  onOpenGoal: (token: string) => void;
};

const ProgressBar: React.FC<{ percent: number }> = ({ percent }) => (
  <div className="h-1 overflow-hidden rounded-full bg-bg-secondary">
    <div
      className="h-full rounded-full bg-text-primary"
      style={{ width: `${percent}%` }}
    />
  </div>
);

const MetaLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    size="xs"
    color="tertiary"
    weight="semibold"
    className="w-16 shrink-0 pt-0.5 tracking-wide uppercase"
  >
    {children}
  </Text>
);

const ConditionDot: React.FC = () => (
  <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-text-teal" />
);

const TreatingChips: React.FC<{
  treating: TreatedCondition[];
  onOpenCondition: (token: string) => void;
}> = ({ treating, onOpenCondition }) => (
  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
    {treating.map((condition) => (
      <button
        key={condition.token}
        type="button"
        data-testid="care-plan-condition-chip"
        onClick={() => onOpenCondition(condition.token)}
        className="flex items-center gap-1.5 rounded-full bg-bg-secondary px-2 py-0.5 text-xs font-medium hover:bg-bg-tertiary"
      >
        <ConditionDot />
        {condition.display}
      </button>
    ))}
  </div>
);

/** Filled status dot matching the Goals card's tones. */
const GoalDot: React.FC<{ goal?: Goal }> = ({ goal }) => {
  const progress = goal ? goalProgress(goal) : undefined;
  return (
    <span
      aria-hidden
      className={cn(
        'size-1.75 shrink-0 rounded-full',
        progress?.color === 'positive'
          ? 'bg-text-positive'
          : progress?.color === 'warning'
            ? 'bg-text-warning'
            : 'bg-text-primary',
      )}
    />
  );
};

const GoalMiniRows: React.FC<{
  goals: LinkedGoal[];
  onOpenGoal: (token: string) => void;
}> = ({ goals, onOpenGoal }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    {goals.map((linked) => {
      const progress = linked.goal ? goalProgress(linked.goal) : undefined;
      return (
        <button
          key={linked.token}
          type="button"
          data-testid="care-plan-goal-mini-row"
          onClick={() => onOpenGoal(linked.token)}
          className="flex w-full items-center gap-2 rounded-sm text-left hover:bg-bg-secondary"
        >
          <GoalDot goal={linked.goal} />
          <Text inline size="xs" className="min-w-0 flex-1 truncate">
            {linked.display}
          </Text>
          {progress && (
            <Text
              inline
              size="xs"
              weight="semibold"
              className={cn(
                'shrink-0',
                progress.color === 'positive'
                  ? 'text-text-positive'
                  : progress.color === 'warning'
                    ? 'text-text-warning'
                    : 'text-text-tertiary',
              )}
            >
              {progress.label}
            </Text>
          )}
        </button>
      );
    })}
  </div>
);

const STEP_CIRCLE: Record<PhaseStep['state'], string> = {
  done: 'bg-text-primary text-bg-primary',
  current: 'bg-text-insights text-bg-primary ring-4 ring-bg-insights-subtle',
  upcoming: 'bg-bg-secondary text-text-tertiary',
};

const PhaseStepper: React.FC<{ steps: PhaseStep[] }> = ({ steps }) => (
  <div className="flex" data-testid="care-plan-phase-stepper">
    {steps.map((step, index) => (
      <div
        key={`${step.title}-${index}`}
        className="relative flex flex-1 flex-col items-center text-center"
      >
        {index < steps.length - 1 && (
          <div
            aria-hidden
            className={cn(
              'absolute top-2.5 left-[calc(50%+0.75rem)] h-0.5 w-[calc(100%-1.5rem)]',
              step.state === 'done' ? 'bg-text-primary' : 'bg-bg-secondary',
            )}
          />
        )}
        <div
          className={cn(
            'relative z-1 flex size-5.5 items-center justify-center rounded-full text-xs font-semibold',
            STEP_CIRCLE[step.state],
          )}
        >
          {step.state === 'done' ? (
            <Icon name="Check" size="xs" strokeWidth={3} />
          ) : (
            index + 1
          )}
        </div>
        <Text
          size="sm"
          weight="medium"
          className={cn(
            'mt-2',
            step.state === 'current'
              ? 'text-text-insights'
              : step.state === 'done'
                ? 'text-text-primary'
                : 'text-text-tertiary',
          )}
        >
          {step.title}
        </Text>
        {step.weeksLabel && (
          <Text size="xs" color="tertiary" className="mt-0.5">
            {step.weeksLabel}
          </Text>
        )}
      </div>
    ))}
  </div>
);

const SubSectionHeader: React.FC<{
  icon: React.ComponentProps<typeof Icon>['name'];
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <Stack
    direction="horizontal"
    gap={2}
    align="center"
    fullWidth={false}
    className="mb-2.5"
  >
    <Icon name={icon} size="xs" className="text-text-tertiary" />
    <Text
      inline
      size="xs"
      color="tertiary"
      weight="semibold"
      className="tracking-wide uppercase"
    >
      {children}
    </Text>
  </Stack>
);

const GoalRows: React.FC<{
  goals: LinkedGoal[];
  onOpenGoal: (token: string) => void;
}> = ({ goals, onOpenGoal }) => (
  <div>
    {goals.map((linked) => {
      const tracking = linked.goal?.target?.[0]?.measure?.text;
      return (
        <button
          key={linked.token}
          type="button"
          data-testid="care-plan-goal-row"
          onClick={() => onOpenGoal(linked.token)}
          className="flex w-full items-start gap-3 rounded-sm py-2.5 text-left not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
        >
          <span className="pt-1.5">
            <GoalDot goal={linked.goal} />
          </span>
          <div className="min-w-0 flex-1">
            <Text size="sm" weight="medium">
              {linked.display}
            </Text>
            {tracking && (
              <Text size="xs" color="tertiary" className="mt-0.5">
                Tracking {tracking}
              </Text>
            )}
          </div>
          {linked.goal && <GoalAchievementTag goal={linked.goal} />}
          <Icon
            name="ChevronRight"
            size="xs"
            className="mt-1 shrink-0 text-text-tertiary"
          />
        </button>
      );
    })}
  </div>
);

const DatedProgress: React.FC<{
  started?: string;
  end?: string;
  weekNow?: number;
  weekTotal?: number;
  percent?: number;
}> = ({ started, end, weekNow, weekTotal, percent }) => (
  <>
    <Stack
      direction="horizontal"
      gap={4}
      align="baseline"
      justify="between"
      className="mb-2"
    >
      {started && (
        <Text inline size="xs" color="tertiary">
          Started:{' '}
          <Text inline size="xs">
            {formatFhirDate(started)}
          </Text>
        </Text>
      )}
      {weekNow !== undefined && weekTotal !== undefined && (
        <Text inline size="xs" color="tertiary">
          Week:{' '}
          <Text inline size="xs">
            {weekNow} of {weekTotal}
          </Text>
        </Text>
      )}
      {end && (
        <Text inline size="xs" color="tertiary">
          Est. end:{' '}
          <Text inline size="xs">
            {formatFhirDate(end)}
          </Text>
        </Text>
      )}
    </Stack>
    {percent !== undefined && <ProgressBar percent={percent} />}
  </>
);

export const CarePlanCard: React.FC<CarePlanCardProps> = ({
  plan,
  serviceRequests,
  goals,
  todayIsoDate,
  expanded,
  onToggle,
  onOpenCondition,
  onOpenGoal,
}) => {
  const token = carePlanToken(plan);
  const steps = useMemo(
    () => planPhaseSteps(plan, serviceRequests),
    [plan, serviceRequests],
  );
  const treating = useMemo(() => treatedConditions(plan), [plan]);
  const linked = useMemo(() => linkedGoals(plan, goals), [plan, goals]);
  const progress = useMemo(
    () => planProgress(plan, todayIsoDate),
    [plan, todayIsoDate],
  );
  const now = currentPhase(steps);
  const phaseNumber = now ? steps.indexOf(now) + 1 : null;

  return (
    <section
      data-testid="care-plan-card"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <button
        type="button"
        onClick={() => onToggle(token)}
        data-testid="care-plan-card-header"
        aria-expanded={expanded}
        className="flex h-14 w-full items-center gap-3 rounded-lg px-4 text-left hover:bg-bg-secondary"
      >
        <Icon
          name="Route"
          size="sm"
          className="shrink-0 text-text-tertiary opacity-75"
        />
        <Text
          inline
          size="sm"
          weight="semibold"
          className="min-w-0 flex-1 truncate"
        >
          {plan.title ?? 'Care plan'}
        </Text>
        {phaseNumber !== null && (
          <Tag
            pill
            subtle
            size="xs"
            color="insights"
            label={`Phase ${phaseNumber} of ${steps.length}`}
          />
        )}
        <Icon
          name="ChevronDown"
          size="xs"
          className={cn(
            'shrink-0 text-text-tertiary transition-transform',
            !expanded && '-rotate-90',
          )}
        />
      </button>

      {!expanded ? (
        <div className="px-4 pb-4">
          <Stack direction="horizontal" gap={3} align="center" className="mb-2">
            <Text
              inline
              size="xs"
              color="tertiary"
              className="min-w-0 flex-1 truncate"
            >
              {now && (
                <>
                  Now:{' '}
                  <Text
                    inline
                    size="xs"
                    weight="semibold"
                    className="text-text-insights"
                  >
                    {now.title}
                  </Text>
                </>
              )}
            </Text>
            {progress && (
              <Text inline size="xs" color="tertiary" className="shrink-0">
                Week{' '}
                <Text inline size="xs">
                  {progress.weekNow} of {progress.weekTotal}
                </Text>
              </Text>
            )}
          </Stack>
          {progress && <ProgressBar percent={progress.percent} />}
          {(treating.length > 0 || linked.length > 0) && (
            <div className="mt-4 flex flex-col gap-2.5">
              {treating.length > 0 && (
                <div className="flex items-start gap-2.5">
                  <MetaLabel>Treating</MetaLabel>
                  <TreatingChips
                    treating={treating}
                    onOpenCondition={onOpenCondition}
                  />
                </div>
              )}
              {linked.length > 0 && (
                <div className="flex items-start gap-2.5">
                  <MetaLabel>Goals</MetaLabel>
                  <GoalMiniRows goals={linked} onOpenGoal={onOpenGoal} />
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 pb-5">
          {treating.length > 0 && (
            <div className="py-1">
              <SubSectionHeader icon="ClipboardList">
                Treating · {treating.length}{' '}
                {treating.length === 1 ? 'condition' : 'conditions'}
              </SubSectionHeader>
              <TreatingChips
                treating={treating}
                onOpenCondition={onOpenCondition}
              />
            </div>
          )}
          {steps.length > 0 && (
            <div className="mt-6">
              <PhaseStepper steps={steps} />
            </div>
          )}
          {(plan.period?.start ?? progress) && (
            <div className="mt-6 border-t border-border-primary pt-5">
              <DatedProgress
                started={plan.period?.start?.slice(0, 10)}
                end={planEndDate(plan)}
                weekNow={progress?.weekNow}
                weekTotal={progress?.weekTotal}
                percent={progress?.percent}
              />
            </div>
          )}
          {linked.length > 0 && (
            <div className="mt-5 border-t border-border-primary pt-5">
              <SubSectionHeader icon="Target">
                Working toward · {linked.length}{' '}
                {linked.length === 1 ? 'goal' : 'goals'}
              </SubSectionHeader>
              <GoalRows goals={linked} onOpenGoal={onOpenGoal} />
            </div>
          )}
        </div>
      )}
    </section>
  );
};
